/**
 * Hooks de Telegram (ADR_007) — admin UI sobre /api/v1/telegram/*.
 *
 * Reusan el patrón de useEcc.ts: react-query para queries (list, bot health,
 * stats) e invalidación automática en onSuccess de las mutations.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  ActualizarReglaRuteoRequest,
  ActualizarUsuarioMenuRequest,
  CrearUsuarioMenuRequest,
  UsuarioMenu,
  UsuarioMenuList,
  CrearReglaRuteoRequest,
  CreateTelegramDestinationRequest,
  ReglaRuteo,
  ReglaRuteoList,
  SimulacionRuteo,
  TelegramBotHealth,
  TelegramDestination,
  TelegramDestinationList,
  TelegramShadowLogFilters,
  TelegramShadowLogList,
  TelegramShadowLogStats,
  TelegramStats,
  TelegramTestRequest,
  TelegramTestResponse,
  UpdateTelegramDestinationRequest,
} from "@/api/types";

const HEALTH_POLL_MS = 60_000; // 1 min — el getMe cuesta poco pero no hay que abusar
const STATS_POLL_MS = 30_000;

export function useTelegramDestinations() {
  return useQuery({
    queryKey: ["telegram", "destinations"],
    queryFn: ({ signal }) =>
      api<TelegramDestinationList>("/api/v1/telegram/destinations", { signal }),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

export function useTelegramBotHealth() {
  return useQuery({
    queryKey: ["telegram", "bot", "health"],
    queryFn: ({ signal }) =>
      api<TelegramBotHealth>("/api/v1/telegram/bot/health", { signal }),
    refetchInterval: HEALTH_POLL_MS,
    placeholderData: (prev) => prev,
  });
}

export function useTelegramStats() {
  return useQuery({
    queryKey: ["telegram", "stats"],
    queryFn: ({ signal }) =>
      api<TelegramStats>("/api/v1/telegram/stats", { signal }),
    refetchInterval: STATS_POLL_MS,
    placeholderData: (prev) => prev,
  });
}

export function useCreateTelegramDestination() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTelegramDestinationRequest) =>
      api<TelegramDestination>("/api/v1/telegram/destinations", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["telegram"] });
    },
  });
}

export function useUpdateTelegramDestination() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      name,
      body,
    }: {
      name: string;
      body: UpdateTelegramDestinationRequest;
    }) =>
      api<TelegramDestination>(`/api/v1/telegram/destinations/${name}`, {
        method: "PATCH",
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["telegram"] });
    },
  });
}

export function useDeleteTelegramDestination() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api<void>(`/api/v1/telegram/destinations/${name}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["telegram"] });
    },
  });
}

export function useTestTelegramDestination() {
  return useMutation({
    mutationFn: (body: TelegramTestRequest) =>
      api<TelegramTestResponse>("/api/v1/telegram/test", {
        method: "POST",
        body,
      }),
  });
}

// ---- ADR_008: Tab Historial ensayo ----

function buildShadowLogQuery(filters: TelegramShadowLogFilters): string {
  // URLSearchParams encodea `+` y caracteres especiales automaticamente —
  // por eso no construimos la query a mano.
  const params = new URLSearchParams();
  if (filters.destination_name)
    params.set("destination_name", filters.destination_name);
  if (filters.event_type) params.set("event_type", filters.event_type);
  if (filters.since) params.set("since", filters.since);
  if (filters.until) params.set("until", filters.until);
  if (filters.page) params.set("page", String(filters.page));
  if (filters.per_page) params.set("per_page", String(filters.per_page));
  return params.toString();
}

export function useTelegramShadowLog(filters: TelegramShadowLogFilters) {
  const qs = buildShadowLogQuery(filters);
  return useQuery({
    queryKey: ["telegram", "shadow-log", filters],
    queryFn: ({ signal }) =>
      api<TelegramShadowLogList>(
        `/api/v1/telegram/shadow-log${qs ? `?${qs}` : ""}`,
        { signal },
      ),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

export function useTelegramShadowLogStats() {
  return useQuery({
    queryKey: ["telegram", "shadow-log", "stats"],
    queryFn: ({ signal }) =>
      api<TelegramShadowLogStats>("/api/v1/telegram/shadow-log/stats", {
        signal,
      }),
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });
}

// ── Reglas de ruteo ─────────────────────────────────────────────────────────

export function useReglasRuteo() {
  return useQuery({
    queryKey: ["telegram", "routing-rules"],
    queryFn: ({ signal }) =>
      api<ReglaRuteoList>("/api/v1/telegram/routing-rules", { signal }),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

/**
 * A donde caeria cada alarma vigente con las reglas de ahora. No manda nada.
 *
 * `enabled` lo controla la pantalla: consultarlo pega al Zabbix, asi que solo
 * corre cuando alguien abre el simulador, no en cada visita a la pestana.
 */
export function useSimulacionRuteo(activo: boolean) {
  return useQuery({
    queryKey: ["telegram", "routing-rules", "simulacion"],
    queryFn: ({ signal }) =>
      api<SimulacionRuteo>("/api/v1/telegram/routing-rules/simulacion", { signal }),
    enabled: activo,
    staleTime: 10_000,
  });
}

function useInvalidarReglas() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["telegram", "routing-rules"] });
  };
}

export function useCrearRegla() {
  const invalidar = useInvalidarReglas();
  return useMutation({
    mutationFn: (body: CrearReglaRuteoRequest) =>
      api<ReglaRuteo>("/api/v1/telegram/routing-rules", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: invalidar,
  });
}

export function useActualizarRegla() {
  const invalidar = useInvalidarReglas();
  return useMutation({
    mutationFn: ({ id, cambios }: { id: number; cambios: ActualizarReglaRuteoRequest }) =>
      api<ReglaRuteo>(`/api/v1/telegram/routing-rules/${id}`, {
        method: "PATCH",
        body: JSON.stringify(cambios),
      }),
    onSuccess: invalidar,
  });
}

export function useBorrarRegla() {
  const invalidar = useInvalidarReglas();
  return useMutation({
    mutationFn: (id: number) =>
      api<void>(`/api/v1/telegram/routing-rules/${id}`, { method: "DELETE" }),
    onSuccess: invalidar,
  });
}

// ── Usuarios del menu ───────────────────────────────────────────────────────

export function useUsuariosMenu() {
  return useQuery({
    queryKey: ["telegram", "menu-users"],
    queryFn: ({ signal }) =>
      api<UsuarioMenuList>("/api/v1/telegram/menu-users", { signal }),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

function useInvalidarUsuariosMenu() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["telegram", "menu-users"] });
  };
}

export function useCrearUsuarioMenu() {
  const invalidar = useInvalidarUsuariosMenu();
  return useMutation({
    mutationFn: (body: CrearUsuarioMenuRequest) =>
      api<UsuarioMenu>("/api/v1/telegram/menu-users", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: invalidar,
  });
}

export function useActualizarUsuarioMenu() {
  const invalidar = useInvalidarUsuariosMenu();
  return useMutation({
    mutationFn: ({ id, cambios }: { id: number; cambios: ActualizarUsuarioMenuRequest }) =>
      api<UsuarioMenu>(`/api/v1/telegram/menu-users/${id}`, {
        method: "PATCH",
        body: JSON.stringify(cambios),
      }),
    onSuccess: invalidar,
  });
}

export function useBorrarUsuarioMenu() {
  const invalidar = useInvalidarUsuariosMenu();
  return useMutation({
    mutationFn: (id: number) =>
      api<void>(`/api/v1/telegram/menu-users/${id}`, { method: "DELETE" }),
    onSuccess: invalidar,
  });
}
